import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, catchError, map, switchMap, tap } from 'rxjs';
import { AutoMatchSignalRequirementsResponseDto } from 'src/app/compliance/models/automatch-signal-requirements-response.model';
import {
    loadCandidateInformation,
    loadCandidateInformationFailure,
    loadCandidateInformationSuccess
} from 'src/app/compliance/store/actions/document-reviewer.actions';
import { HubConnectionService } from 'src/app/shared/services/realtime-connection.service';
import { ComplianceReportHubMessage } from 'src/app/shared/services/realtime-constants';
import { ComplianceReportClient } from '../../api/compliance-report-client.service';
import {
    autoMatchResponseFailure,
    autoMatchResponseSuccess,
    markAsReviewed,
    markAsReviewedFailure,
    markAsReviewedSuccess
} from '../actions/compliance-report.actions';
import { automatchSignalReceived } from 'src/app/compliance/store/actions/compliance-report-new.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { Owner } from 'src/app/shared/helpers/owner.decorator';

@Owner('Mic Drop')
@Injectable()
export class ComplianceReportEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _hubConnectionService: HubConnectionService,
        private readonly complianceReportClient: ComplianceReportClient
    ) {}

    getCandidateInfoById$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadCandidateInformation),
            switchMap((action) => {
                return this.complianceReportClient.getComplianceReportInfoByCandidateId(action.candidateId).pipe(
                    map((result) => loadCandidateInformationSuccess({ candidateInfo: result })),
                    catchError((result) => of(loadCandidateInformationFailure({ error: result })))
                );
            })
        )
    );

    getAutoMatchResponseFromMessagesOfSignalR$ = createEffect(() =>
        this._hubConnectionService.complianceReportHub
            .messagesOf<AutoMatchSignalRequirementsResponseDto>(ComplianceReportHubMessage.AUTOMATCH_RESPONSE)
            .pipe(
                switchMap((automatchData) =>
                    of(
                        autoMatchResponseSuccess({ data: automatchData }),
                        automatchSignalReceived({ automatchData: automatchData })
                    )
                ),
                catchError((er) => of(autoMatchResponseFailure({ error: er })))
            )
    );

    markAsReviewed$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewed),
            switchMap((action) =>
                this.complianceReportClient.markAsReviewed(action.contractId).pipe(
                    map((status) => markAsReviewedSuccess({ contractId: action.contractId })),
                    catchError((result) => {
                        console.error(result);
                        return of(markAsReviewedFailure({ error: result }));
                    })
                )
            )
        )
    );

    markAsReviewedSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewedSuccess),
            map((action) => showToasterMessage({ message: 'Successfully updated Compliance Status and requirements.' }))
        )
    );

    markAsReviewedFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewedFailure),
            tap((action) => console.error('Mark As Reviewed Failed: ', action.error)),
            map(() => showToasterMessage({ message: 'Mark As Reviewed Failed' }))
        )
    );
}
